import { useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import { SupportCasesItem } from '../types';
import { getSelectedAccountIds, getSelectedAccountIdsForUrl, getUser } from './User';
import { Permission } from './GeneralStore';
import { CaseRequest, fetchCases as apiFetchCases, postCase } from '../Api/Cases';
import { formatSupportCaseDate, getCaseClosedDate } from '../utils/helpers';
import { addError } from './Error';

export interface UseSupportCaseReturn {
  setCasesData: (cases: SupportCasesItem[]) => void;
  fetchCases: (forceRefresh?: boolean) => Promise<void>;
  clear: () => void;
  cases: SupportCasesItem[];
}

export const OPEN_STATUS = ['New', 'Assigned', 'On Hold', 'In Progress', 'Escalated', 'Waiting Customer Confirmation'];

export const isSupportCase = (data: unknown): data is SupportCasesItem => {
  return typeof data === 'object' && data !== null && !!(data as SupportCasesItem)?.sfId;
};

const defaultCaseData: SupportCasesItem[] = [];

const subject = new BehaviorSubject<SupportCasesItem[]>(defaultCaseData);
const loadingSubject = new BehaviorSubject<boolean>(false);
export const newCaseCreatedSubject = new BehaviorSubject<boolean>(false);
const newCaseProcessingSubject = new BehaviorSubject<boolean>(false);

const emitErrorState = (message?: string): void => addError('support-case', message);

export const getCaseLoadingSubject = (): BehaviorSubject<boolean> => loadingSubject;
export const getNewCaseProcessingSubject = (): BehaviorSubject<boolean> => newCaseProcessingSubject;
export const getNewCaseCreatedSubject = (): BehaviorSubject<boolean> => newCaseCreatedSubject;
export const getCaseSubject = (): BehaviorSubject<SupportCasesItem[]> => subject;

export const getCases = (): SupportCasesItem[] => subject.value;
export const getCaseById = (id: string): SupportCasesItem | undefined => subject.value.find((case_) => case_.id === id);
export const getCasesByAssetId = (id: string): SupportCasesItem[] =>
  subject.value.filter((case_) => case_.Associated_Inventory__c === id || case_.portalAssetId === id);

// The list and the single-case read both arrive raw; the grid's date columns expect these formats.
const shapeCase = (case_: SupportCasesItem): SupportCasesItem => ({
  ...case_,
  dateTimeClosed: getCaseClosedDate(case_),
  dateTimeOpened: formatSupportCaseDate(case_.dateTimeOpened)
});

export const addCase = async (casePayload: CaseRequest): Promise<SupportCasesItem | void> => {
  newCaseProcessingSubject.next(true);
  const { data, error } = await postCase(casePayload);

  if (data) {
    emitErrorState();
    const selectedAccounts = getSelectedAccountIds();
    if (casePayload?.AccountId && selectedAccounts?.includes(casePayload?.AccountId)) {
      subject.next([shapeCase(data[0]), ...getCases()]);
    }

    newCaseCreatedSubject.next(true);
    newCaseProcessingSubject.next(false);

    return data[0];
  }

  if (error) {
    emitErrorState(error.message);
    newCaseProcessingSubject.next(false);
  }
};

// Module-level so the shared grid column defs, which have no hook in scope, can refresh the list.
export const fetchCases = async (forceRefresh = false): Promise<void> => {
  if (loadingSubject.getValue()) return;
  if (subject.getValue().length > 0 && !forceRefresh) return;

  const userPermissions = (getUser()?.permissions || []).map((permission) => permission?.name || permission);
  if (!userPermissions.includes(Permission.VIEW_CASES)) return;
  loadingSubject.next(true);
  try {
    const { data, error } = await apiFetchCases(getSelectedAccountIdsForUrl());
    if (data) {
      emitErrorState();
      subject.next(data.map(shapeCase));
    }

    if (error) {
      subject.next(defaultCaseData);
      emitErrorState(error.message);
    }
  } catch (e) {
    // A throw here would otherwise wedge the loading flag on, and every reader of the store waits on it.
    emitErrorState(e instanceof Error ? e.message : String(e));
    subject.next(defaultCaseData);
  } finally {
    loadingSubject.next(false);
  }
};

// Row-level refresh: a list fetch flips the loading flag, and the grid renders a spinner in its place.
export const fetchCaseById = async (id: string): Promise<void> => {
  const { data, error } = await apiFetchCases(getSelectedAccountIdsForUrl(), { caseId: id });
  const case_ = Array.isArray(data) ? data[0] : (data as SupportCasesItem | null);
  if (!case_) return emitErrorState(error?.message || 'Case not found');

  subject.next(subject.getValue().map((item) => (item.id === id ? { ...item, ...shapeCase(case_) } : item)));
  emitErrorState();
};

export const useSupportCases = (): UseSupportCaseReturn => {
  const [cases, setCases] = useState(defaultCaseData);

  const handleFetchCases = async (forceRefresh = false): Promise<void> => {
    await fetchCases(forceRefresh);
    setCases(subject.getValue());
  };

  const setCasesData = (cases: SupportCasesItem[]) => subject.next(cases);
  const clear = () => subject.next(defaultCaseData);

  return { setCasesData, fetchCases: handleFetchCases, clear, cases };
};
